// Settings (F3; tabs since S-1): the Hub addresses, the runner's limits, the run log's retention and the error-rate
// alert rule. Fixtures: persisted in this browser. API (LH-B1): read at sign-in, saved with
// PUT /settings (the API writes the History line).
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { DEFAULT_SETTINGS } from '@/fixtures/flows'
import { api, apiMode } from '@/lib/hubApi'
import { remote, useHubStore } from '@/store/hubStore'
import type { PortalSettings } from '@/types'

interface SettingsState {
  settings: PortalSettings
  update: (patch: Partial<PortalSettings>, who: string) => void
  reset: () => void
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      settings: DEFAULT_SETTINGS,
      update: (patch, who) => {
        const settings = { ...get().settings, ...patch }
        set({ settings })
        if (apiMode()) remote(() => api<PortalSettings>('PUT', '/settings', settings), (s) => set({ settings: s }))
        else useHubStore.getState().addHistory(who, '', 'Settings', `Changed: ${Object.keys(patch).join(', ')}`)
      },
      reset: () => {
        if (apiMode()) { remote(() => api<PortalSettings>('GET', '/settings'), (s) => set({ settings: s })); return }
        set({ settings: DEFAULT_SETTINGS })
      },
    }),
    {
      name: apiMode() ? 'lender-hub-portal-settings-api' : 'lender-hub-portal-settings',
      version: 3,
      partialize: (s) => (apiMode() ? ({} as Partial<SettingsState>) : s),
      // a browser that saved an older shape: the known keys kept (new ones from the defaults, hubUrls since LH-L), the
      // removed ones dropped (S-1: cacheTimeS, bundlesKeptExtra, bundleDays, alertEmails)
      merge: (persisted, current) => {
        const p = (persisted as Partial<SettingsState> | undefined) ?? {}
        const old = (p.settings ?? {}) as Partial<PortalSettings>
        const settings = Object.fromEntries(Object.entries(DEFAULT_SETTINGS).map(([k, v]) => [k, old[k as keyof PortalSettings] ?? v])) as unknown as PortalSettings
        return { ...current, ...p, settings }
      },
    },
  ),
)
